import type { Student, Group, Settings, Constraints } from "../../types/models";
import { scoreGroup } from "../scoring";
import { buildApartPairs } from "../constraints";

/**
 * Hill climbing optimization via beneficial swaps
 * Performs multiple passes, trying all pairwise swaps between groups
 * Respects locked students (won't move them)
 */
export function hillClimbingOptimization(
    groups: Group[],
    students: Student[],
    constraints: Constraints,
    settings: Settings,
    passes: number = 3
): void {
    const apartPairs = buildApartPairs(constraints);
    const classMean = students.reduce((sum, s) => sum + s.skill, 0) / Math.max(1, students.length);
    const classStats = { meanSkill: classMean };

    // Build set of locked student IDs for quick lookup
    const lockedStudentIds = new Set(
        students.filter(s => s.lockedToGroupId).map(s => s.id)
    );

    for (let pass = 0; pass < passes; pass++) {
        let swapsMade = 0;

        // Try all pairs of groups
        for (let i = 0; i < groups.length; i++) {
            for (let j = i + 1; j < groups.length; j++) {
                const groupA = groups[i];
                const groupB = groups[j];
                if (!groupA || !groupB) continue;

                // Try all pairwise student swaps (skip locked students)
                for (const studentAId of groupA.memberIds) {
                    if (lockedStudentIds.has(studentAId)) continue;
                    for (const studentBId of groupB.memberIds) {
                        if (lockedStudentIds.has(studentBId)) continue;
                        // Evaluate swap
                        if (trySwap(studentAId, studentBId, groupA, groupB, apartPairs, students, classStats, settings)) {
                            swapsMade++;
                        }
                    }
                }
            }
        }

        // If no swaps were made this pass, we've reached a local optimum
        if (swapsMade === 0) break;
    }
}

/**
 * Try swapping two students between groups
 * Returns true if swap was performed, false otherwise
 */
function trySwap(
    studentAId: string,
    studentBId: string,
    groupA: Group,
    groupB: Group,
    apartPairs: Set<string>,
    students: Student[],
    classStats: { meanSkill: number },
    settings: Settings
): boolean {
    // Create hypothetical groups after swap
    const newA = groupA.memberIds.filter(id => id !== studentAId);
    newA.push(studentBId);

    const newB = groupB.memberIds.filter(id => id !== studentBId);
    newB.push(studentAId);

    // Check size constraints
    if (newA.length < settings.minSize || newA.length > settings.maxSize) return false;
    if (newB.length < settings.minSize || newB.length > settings.maxSize) return false;

    // Check must-apart constraints
    if (violatesApart(studentBId, newA, apartPairs)) return false;
    if (violatesApart(studentAId, newB, apartPairs)) return false;

    // Score before and after
    const scoreBefore =
        scoreGroup(groupA, students, classStats, settings, settings.targetSize) +
        scoreGroup(groupB, students, classStats, settings, settings.targetSize);

    const tempGroupA: Group = { ...groupA, memberIds: newA };
    const tempGroupB: Group = { ...groupB, memberIds: newB };

    const scoreAfter =
        scoreGroup(tempGroupA, students, classStats, settings, settings.targetSize) +
        scoreGroup(tempGroupB, students, classStats, settings, settings.targetSize);

    // Only swap if it improves total score
    if (scoreAfter > scoreBefore) {
        groupA.memberIds = newA;
        groupB.memberIds = newB;
        return true;
    }

    return false;
}

/**
 * Check if student violates must-apart with anyone in the group
 */
function violatesApart(studentId: string, groupMemberIds: string[], apartPairs: Set<string>): boolean {
    for (const memberId of groupMemberIds) {
        if (memberId === studentId) continue;
        const [a, b] = [studentId, memberId].sort();
        if (apartPairs.has(`${a}|${b}`)) {
            return true;
        }
    }
    return false;
}
